<template>
  <div
    class="relative w-full mb-2 shadow-md rounded-md overflow-hidden"
    @mouseenter="onMouseenter"
    @mouseleave="onMouseleave"
  >
    <div class="bg-white pl-4 pr-10 py-3 text-sm border-2 rounded-md" :class="border">
      <div v-html="content"></div>
      <button class="absolute p-1 right-2 top-2" @click="message.unmount()">
        <Icon
          type="close"
          size="3"
        ></Icon>
      </button>
    </div>
    <div class="absolute bottom-0 left-0 w-full h-[6px]">
      <div class="progress" :class="color" :style="state"></div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive } from 'vue';
import { useMessage } from '@/message';
import parseTime from '@/helpers/parseTime';
import countdown from '@/helpers/countdown';
import Icon from './Icon.vue';

const props = defineProps({
  content: {
    type: String,
    default: null,
  },
  theme: {
    type: String,
    default: 'default',
  },
  timeout: {
    type: String,
    default: null,
  },
});

const message = useMessage();

const color = computed(() => {
  return {
    default: 'bg-gray-100',
    positive: 'bg-green-500',
    negative: 'bg-red-500',
  }[props.theme];
});

const border = computed(() => {
  return {
    default: 'border-gray-100',
    positive: 'border-green-500',
    negative: 'border-red-500',
  }[props.theme];
});

const state = reactive({
  animationPlayState: 'running',
  animationDuration: props.timeout,
  mouseEvents: false,
});

const time = parseTime(props.timeout, 0);

const { start, stop, pause } = countdown(time, () => {
  message.unmount();
});

function onMouseenter() {
  if (!state.mouseEvents) return;
  state.animationPlayState = 'paused';
  pause();
}

function onMouseleave() {
  if (!state.mouseEvents) return;
  state.animationPlayState = 'running';
  time && start();
}

onMounted(() => {
  state.mouseEvents = true;
  time && start();
});

onUnmounted(() => {
  state.mouseEvents = false;
  stop();
});
</script>

<style scoped>
.progress {
  animation: progress linear;
  position: absolute;
  height: 100%;
  right: 0;
  top: 0;
}

@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}
</style>
